import React from "react";
import {
  Avatar,
  Typography,
  Divider,
  Box,
  Chip,
  Link,
  Card,
  Grid,
} from "@mui/material";
import WcIcon from "@mui/icons-material/Wc";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonIcon from "@mui/icons-material/Person";
import { API_BASE_URL } from "../../../utils/api";
import ArchitecturalRequestTimeline from "../ArchitecturalRequestTimeline";
import MiniMap from "../../shared/MiniMap";

const MemberCard = ({ member, id }) => {
  if (!member) return null;

  const getFormattedDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const photoUrl = `${API_BASE_URL}/images/${encodeURIComponent(
    member.PhotoGraphicFileName?.trim() || ""
  )}`;

  const address = member.FullAddress?.trim() || "N/A";
  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

  const phone = member.PhoneNumber?.trim() || null;
  const altPhone = member.AlternatePhoneNumber?.trim() || null;

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const infoRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.75,
    minWidth: 0,
    height: "100%",
  };

  const iconWrapSx = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const labelSx = {
    color: "#8a94a6",
    display: "block",
    lineHeight: 1.2,
    mb: 0.25,
  };

  const valueSx = {
    color: "#223142",
    fontWeight: 600,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  };

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
          textAlign: "center",
        }}
      >
        <Avatar
          src={photoUrl}
          alt={member.FirstName}
          sx={{
            width: 112,
            height: 112,
            margin: "0 auto",
            mb: 1.75,
            border: "4px solid rgba(47,99,173,0.16)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
          }}
        />

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#223142",
            lineHeight: 1.2,
          }}
        >
          {`${member.FirstName || ""} ${member.LastName || ""}`.trim() || "Member"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
            mt: 0.5,
          }}
        >
          Member Code: {member.ClubMemberCode || "N/A"}
        </Typography>

        <Box sx={{ mt: 1.5 }}>
          <Chip
            label={member.ClubStatusRuleCode || "Unknown"}
            color="success"
            size="small"
            icon={<VerifiedUserIcon sx={{ color: "white !important" }} />}
            sx={{
              fontWeight: 700,
              height: 30,
              borderRadius: "999px",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <PersonIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Member
                </Typography>
                <Typography variant="body2" sx={valueSx}>
                  {member.ClubMemberCode || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <WcIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Gender
                </Typography>
                <Typography variant="body2" sx={valueSx}>
                  {member.Gender || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Birth Date
                </Typography>
                <Typography variant="body2" sx={valueSx}>
                  {getFormattedDate(member.BirthDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Join Date
                </Typography>
                <Typography variant="body2" sx={valueSx}>
                  {getFormattedDate(member.JoinDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <PhoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Phone
                </Typography>
                {phone ? (
                  <Link
                    href={`tel:${phone}`}
                    underline="hover"
                    sx={{
                      color: "#2f63ad",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      wordBreak: "break-word",
                    }}
                  >
                    {phone}
                  </Link>
                ) : (
                  <Typography variant="body2" sx={valueSx}>
                    N/A
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <PhoneAndroidIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Alt Phone
                </Typography>
                {altPhone ? (
                  <Link
                    href={`tel:${altPhone}`}
                    underline="hover"
                    sx={{
                      color: "#2f63ad",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      wordBreak: "break-word",
                    }}
                  >
                    {altPhone}
                  </Link>
                ) : (
                  <Typography variant="body2" sx={valueSx}>
                    N/A
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={infoRowSx}>
              <Box sx={iconWrapSx}>
                <LocationOnIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={labelSx}>
                  Address
                </Typography>
                {mapsLink ? (
                  <Link
                    href={mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      color: "#2f63ad",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {address}
                  </Link>
                ) : (
                  <Typography variant="body2" sx={valueSx}>
                    N/A
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {member?.ClubMemberCode && (
            <Grid item xs={12}>
              <MiniMap clubMemberCode={member.ClubMemberCode} height={160} />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 1.25,
            }}
          >
            Request Timeline
          </Typography>

          <Box
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              p: 1.5,
            }}
          >
            <ArchitecturalRequestTimeline architectural_request_id={id} />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default MemberCard;
